/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  POST_DBS_FILES,
  GET_DBS_FILES_BY_FILE_ID,
  DELETE_DBS_FILES_BY_FILE_ID,
} from './actionTypes';

import { postDbsFilesUtil, getDbsFilesByFileIdUtil, deleteDbsFilesByFileIdUtil } from './utils';

/**
 * Contains the action creator functions that update the DocumentService slice of the store.
 *
 * @namespace DocumentServiceActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/dbs/files
   *
   * @param {NewSPMFileUploadContents} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof DocumentServiceActions
   */
  static postDbsFiles = (dispatch, newSPMFileUploadContents, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postDbsFilesUtil.bind(null, newSPMFileUploadContents),
      POST_DBS_FILES,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postDbsFiles.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof DocumentServiceActions
   */
  static resetPostDbsFiles = (dispatch, data) => {
    const json = {
      type: POST_DBS_FILES.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postDbsFiles.
   *
   * @param {function} dispatch
   * @function
   * @memberof DocumentServiceActions
   */
  static resetPostDbsFilesError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_DBS_FILES.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/dbs/files/{fileId}
   *
   * @param {string} fileId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof DocumentServiceActions
   */
  static getDbsFilesByFileId = (dispatch, fileId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getDbsFilesByFileIdUtil.bind(null, fileId),

      GET_DBS_FILES_BY_FILE_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getDbsFilesByFileId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof DocumentServiceActions
   */
  static resetGetDbsFilesByFileId = (dispatch, data) => {
    const json = {
      type: GET_DBS_FILES_BY_FILE_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getDbsFilesByFileId.
   *
   * @param {function} dispatch
   * @function
   * @memberof DocumentServiceActions
   */
  static resetGetDbsFilesByFileIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_DBS_FILES_BY_FILE_ID.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/dbs/files/{fileId}
   *
   * @param {string} fileId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof DocumentServiceActions
   */
  static deleteDbsFilesByFileId = (dispatch, fileId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      deleteDbsFilesByFileIdUtil.bind(null, fileId),
      DELETE_DBS_FILES_BY_FILE_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to deleteDbsFilesByFileId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof DocumentServiceActions
   */
  static resetDeleteDbsFilesByFileId = (dispatch, data) => {
    const json = {
      type: DELETE_DBS_FILES_BY_FILE_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to deleteDbsFilesByFileId.
   *
   * @param {function} dispatch
   * @function
   * @memberof DocumentServiceActions
   */
  static resetDeleteDbsFilesByFileIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, DELETE_DBS_FILES_BY_FILE_ID.failureAction);
  };
}
